import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../context/AuthContext';
import {
  getPublicProductsSubscriptionAmount,
  getPublicProductsSubscriptionCurrency,
  isPublicProductsSubscriptionRequired,
} from '../../config/publicProductsSubscription';
import { createLogger } from '../../utils/logger';
import './ProductsAnalyticsHero.css';

const logger = createLogger('ProductsAnalyticsHero');

const ANALYTICS_FEATURES = [
  { icon: 'fa-trophy', label: 'Best-selling products' },
  { icon: 'fa-fire', label: 'Trending items' },
  { icon: 'fa-layer-group', label: 'Top categories' },
  { icon: 'fa-chart-line', label: 'Price analysis across supermarkets' },
] as const;

/**
 * Hero banner on the home page advertising the public /products catalog and
 * subscriber-only per-product analytics. CTA routes to /products; individual
 * analysis pages present the subscription paywall when the gate is enabled.
 */
export default function ProductsAnalyticsHero() {
  const { currentUser, userDocId } = useAuth();
  const subscriptionRequired = isPublicProductsSubscriptionRequired();
  const subscriptionAmount = getPublicProductsSubscriptionAmount();
  const subscriptionCurrency = getPublicProductsSubscriptionCurrency();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!subscriptionRequired || !currentUser || !userDocId) {
      setIsSubscribed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'Users', userDocId));
        if (!cancelled) {
          setIsSubscribed(snap.exists() && snap.data()?.products_subscription_active === true);
        }
      } catch (err) {
        logger.error('Failed to read subscription status:', err);
        if (!cancelled) setIsSubscribed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subscriptionRequired, currentUser, userDocId]);

  const ctaLabel = isSubscribed
    ? 'View Your Analytics Dashboard'
    : subscriptionRequired
      ? 'Subscribe for Analytics'
      : 'Explore Product Analytics';

  return (
    <section
      className="products-analytics-hero text-center px-3 py-4 py-md-5 mb-5"
      aria-labelledby="products-analytics-hero-title"
      data-testid="products-analytics-hero"
    >
      <span className="products-analytics-hero-badge mb-3">
        <i className="fas fa-chart-pie" aria-hidden />
        Product Analytics
      </span>
      <h2 id="products-analytics-hero-title" className="products-analytics-hero-title mb-3">
        Shop Smarter with Product Analytics
      </h2>
      <p className="products-analytics-hero-subtitle lead mx-auto mb-4">
        Browse every product for free, then subscribe to unlock per-product analytics —
        see what is selling best, what is trending, and how prices compare across supermarkets.
      </p>
      <ul className="products-analytics-hero-features mb-4">
        {ANALYTICS_FEATURES.map((feature) => (
          <li key={feature.icon} className="products-analytics-hero-feature">
            <i className={`fas ${feature.icon}`} aria-hidden />
            <span>{feature.label}</span>
          </li>
        ))}
      </ul>
      <Button
        as={Link as any}
        to="/products"
        size="lg"
        className="products-analytics-hero-cta px-4 py-2"
      >
        <i className={`fas ${isSubscribed ? 'fa-chart-bar' : 'fa-unlock'} me-2`} aria-hidden />
        {ctaLabel}
      </Button>
      {subscriptionRequired && !isSubscribed && (
        <p className="products-analytics-hero-note mt-3 mb-0">
          One-time payment of {subscriptionCurrency} ${Number(subscriptionAmount).toFixed(2)} via
          WiPay — unlocks individual product analytics.
        </p>
      )}
    </section>
  );
}
